import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import trans from 'translate';
import { createBscscanLink, createEtherscanLink, createTokenLink } from 'services/utilsService';
import { useLocation } from 'react-router-dom';
import { getCoinConverterRequest } from '../../../../services/generalService';
import TokenVerifiedOnTokenLists from '../../TokenVerifiedOnTokenLists/TokenVerifiedOnTokenLists';
import TokenWithNetwork from '../../TokenWithNetwork/TokenWithNetwork';
import Slider from '../../Slider/Slider';

import LockIcon from '../../Icons/LockIcon';
import TrashcanIcon from '../../Icons/TrashcanIcon';
import { formatTokenName } from '../../../../utils/format';
import TokenPrice from './TokenPrice/TokenPrice';
import './SliderToken.scss';
import { getValueByMultiProps } from '../../../../utils/mapper';
import { displayFloats } from '../../../../utils/numbersDisplay';

export const DeleteIcon = (props) => (
  <div className="delete-wrapper action-item" {...props}>
    <TrashcanIcon />
  </div>
);

const SliderToken = ({
  token,
  allTokens,
  handleTokenSliderChange,
  handleTokenLock,
  handleTokenDelete,
  sliderCheck,
  renderedAction,
  ethLeftover,
  scan,
  editMode,
  isVpool,
}) => {
  const [sliderVal, setSliderVal] = useState(token.percentage);
  const [sliderVal2, setSliderVal2] = useState(token.percentage);
  const tokenName = formatTokenName(token);
  const location = useLocation();

  const getMultiProp = (token, key) => getValueByMultiProps(token, [`token_${key}`, key]);
  const getAddress = (token) => getMultiProp(token, 'address');

  const link = useMemo(
    () => createTokenLink(getAddress(token), token.network, scan),
    [token.address, token.network, scan],
  );

  // When the val in the reducer is updated, update the local val
  useEffect(() => {
    setSliderVal(token.percentage);
  }, [token.percentage, setSliderVal, allTokens]);

  // Save new val to reducer after the slide is finished
  const handleTokenSliderChangeOnAfterCallback = useCallback(
    (newVal) => {
      handleTokenSliderChange(newVal, token);
    },
    [token, handleTokenSliderChange],
  );
  // Check if the slider can be moved further
  const handleTokenSliderChangeCallback = useCallback(
    (newVal) => {
      const max = sliderVal2 + ethLeftover;
      const value = newVal > max ? max : newVal;
      const changes = sliderCheck(token, value, [...allTokens]);

      const someTokenUnderMin = changes.find(({ min, percentage }) => percentage < min);
      const someTokenUnderZero = changes.find(({ min, percentage }) => percentage < 0);

      const total = changes.reduce((acc, { percentage }) => acc + percentage, 0);
      const totalIsNan = Number.isNaN(total);

      if (!totalIsNan && !someTokenUnderZero && value >= (token.min || 0)) {
        setSliderVal(value);
      }
    },
    [sliderCheck, token, allTokens, ethLeftover, sliderVal2],
  );

  const handleTokenSliderOnBeforeCallback = useCallback(
    (val) => {
      setSliderVal2(val);
    },
    [setSliderVal2],
  );

  const handleTokenLockCallback = useCallback(() => handleTokenLock(token), [token, handleTokenLock]);
  const handleTokenDeleteCallback = useCallback(() => handleTokenDelete(token), [token, handleTokenDelete]);

  return (
    <>
      {/* {token.percentage === 0 && token.name === 'Tether' ? null : ( */}
      {/* <div className="section-title first-section font-reg show-at-m-screen">{trans('common.token_name')}</div> */}
      <div className="slider-token-wrapper">
        <div className="slider-token-info-container">
          <div className="token-info-wrapper first-section">
            <div className="token-container relative">
              <TokenWithNetwork token={token} />
              <div className="token-name-wrapper">
                <span className="token-name">{getMultiProp(token, 'name')}</span>
                <TokenPrice token={token}>
                  {({ usdEqValue }) => (
                    <>
                      <span className="token-price-in-usd">~${usdEqValue}</span>
                    </>
                  )}
                </TokenPrice>
              </div>
            </div>
          </div>
          <div className="explorer-link-wrapper">
            {link ? (
              <a target="_blank" rel="noopener noreferrer" href={link} className="explorer-link font-md">
                {token.symbol || token.token_symbol}
              </a>
            ) : (
              <div className="explorer-symbol font-md">{token.symbol || token.token_symbol}</div>
            )}
          </div>

          <div className="label-wrapper">
            <div className="section-title font-reg show-at-m-screen">{trans('common.allocation')}</div>
            {!location.pathname.includes('/create') && (
              <div className="section-title font-reg show-at-m-screen">{trans('modal.limit_stopLoss_label')}</div>
            )}
          </div>
          <div className="percentage-wrapper">
            <span className="percentage-value">{displayFloats(sliderVal)}%</span>
            <div className="actions-wrapper last-section">
              {!renderedAction?.() ? (
                <>
                  <div
                    className={clsx('lock-wrapper action-item', { locked: token.locked })}
                    onClick={handleTokenLockCallback}
                  >
                    <LockIcon />
                  </div>

                  {!token.deleteDisabled && <DeleteIcon onClick={handleTokenDeleteCallback} />}
                </>
              ) : (
                renderedAction?.(token)
              )}
            </div>
          </div>
        </div>

        <Slider
          value={sliderVal}
          disabled={token.locked || token.symbol === 'eth'}
          onChange={handleTokenSliderChangeCallback}
          onAfterChange={handleTokenSliderChangeOnAfterCallback}
          onBeforeChange={handleTokenSliderOnBeforeCallback}
          defaultValue={sliderVal}
          min={editMode ? 0 : 1}
        />

        <div className="slider-token-verified">
          <TokenVerifiedOnTokenLists token={token} />

          {token.additionalText && (
            <div className="slider-token-additional-text font-reg">
              {isVpool
                ? trans('vpool.must_contain_at_least', { num: token.min, token: token.symbol })
                : trans(token.additionalText, { num: token.min, token: token.symbol })}
            </div>
          )}
        </div>
      </div>
      {/* ) } */}
    </>
  );
};

SliderToken.propTypes = {
  editMode: PropTypes.bool,
  ethLeftover: PropTypes.number.isRequired,
  token: PropTypes.object.isRequired,
  allTokens: PropTypes.array.isRequired,
  handleTokenSliderChange: PropTypes.func.isRequired,
  handleTokenLock: PropTypes.func.isRequired,
  handleTokenDelete: PropTypes.func.isRequired,
  sliderCheck: PropTypes.func.isRequired,
  renderedAction: PropTypes.func.isRequired,
  scan: PropTypes.string.isRequired,
  isVpool: PropTypes.bool,
};

SliderToken.defaultProps = {
  editMode: false,
  isVpool: null,
};

const mapStateToProps = ({ general, createHPool }) => ({
  // TODO remove the link value when BE send scan url at staging
  scan: general.scan || 'https://testnet.bscscan.com/',
  isVpool: createHPool?.isVpool,
});

export default connect(mapStateToProps)(SliderToken);
